/**
 * Tour progress per user, kept in this browser's localStorage under the user id. Not on the server: the progress is a
 * convenience, it carries no business data and losing it only means a tour is offered again. Storage may be
 * unavailable (private mode, blocked site data): then the progress lives in memory for the session and every read
 * falls back to an empty record, so a failure here never blocks a screen.
 */

export interface ActiveTour {
  tourId: string;
  step: number;
}

export interface TourProgress {
  /** tourId → moment the last step was reached (ISO 8601). */
  completed: Record<string, string>;
  /** tourId → step index where the person left the tour, to resume from there. */
  paused: Record<string, number>;
  /** The tour on screen, restored after a reload. */
  active: ActiveTour | null;
  /** The first-visit offer of the general tour was answered (started, or "Agora não"). */
  offerDismissed: boolean;
}

const PREFIX = 'ana.tour.v1.';
export const storageKey = (userId: string) => `${PREFIX}${userId}`;

export const emptyProgress = (): TourProgress => ({
  completed: {},
  paused: {},
  active: null,
  offerDismissed: false,
});

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Reads only well-formed fields: a value edited by hand or left by an older version is ignored, not trusted. */
export function parseProgress(raw: string | null): TourProgress {
  const p = emptyProgress();
  if (!raw) return p;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return p;
  }
  if (!isRecord(v)) return p;
  if (isRecord(v.completed))
    for (const [k, at] of Object.entries(v.completed)) if (typeof at === 'string') p.completed[k] = at;
  if (isRecord(v.paused))
    for (const [k, n] of Object.entries(v.paused))
      if (Number.isInteger(n) && (n as number) >= 0) p.paused[k] = n as number;
  if (
    isRecord(v.active) &&
    typeof v.active.tourId === 'string' &&
    Number.isInteger(v.active.step) &&
    (v.active.step as number) >= 0
  )
    p.active = { tourId: v.active.tourId, step: v.active.step as number };
  p.offerDismissed = v.offerDismissed === true;
  return p;
}

export function loadProgress(userId: string | undefined): TourProgress {
  if (!userId) return emptyProgress();
  try {
    return parseProgress(localStorage.getItem(storageKey(userId)));
  } catch {
    return emptyProgress();
  }
}

export function saveProgress(userId: string | undefined, p: TourProgress): void {
  if (!userId) return;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(p));
  } catch {
    /* storage unavailable: the progress is simply not remembered after this session */
  }
}

// Transitions, pure so the rules are tested without a screen.

export function started(p: TourProgress, tourId: string, step: number): TourProgress {
  return { ...p, active: { tourId, step }, offerDismissed: true };
}

export function moved(p: TourProgress, step: number): TourProgress {
  return p.active ? { ...p, active: { ...p.active, step } } : p;
}

/** Leaving before the end keeps the step, so the tour resumes there. */
export function left(p: TourProgress): TourProgress {
  if (!p.active) return p;
  return { ...p, active: null, paused: { ...p.paused, [p.active.tourId]: p.active.step } };
}

export function finished(p: TourProgress, now: Date): TourProgress {
  if (!p.active) return p;
  const { [p.active.tourId]: _done, ...paused } = p.paused;
  return { ...p, active: null, paused, completed: { ...p.completed, [p.active.tourId]: now.toISOString() } };
}

export function offerDismissed(p: TourProgress): TourProgress {
  return { ...p, offerDismissed: true };
}

export type TourStatus = 'novo' | 'em-andamento' | 'concluido';

export function statusOf(p: TourProgress, tourId: string): TourStatus {
  if (p.active?.tourId === tourId || p.paused[tourId] !== undefined) return 'em-andamento';
  return p.completed[tourId] ? 'concluido' : 'novo';
}
